<template>
  <div class="media-field">
    <!-- label de verdade: é nele que o ::after de .required/.unique pendura o marcador -->
    <label class="ontos-field-label">{{ label }}</label>

    <!-- As três caixas levam key distinta de propósito: são três estados, e cada troca
         monta uma caixa nova em vez de remendar a anterior. No Vuetify 2 isso era
         contorno de defeito — o v-sheet copiava os listeners por watcher, e a caixa
         vazia reaproveitada herdava os da preenchida, travando o clique de escolher
         arquivo. O defeito saiu com o Vuetify 4; a separação continua a mais legível. -->
    <v-sheet v-if="busy" key="busy" border rounded
             class="media-field-box d-flex align-center justify-center" :style="boxStyle">
      <v-progress-circular indeterminate color="primary" size="36"/>
    </v-sheet>

    <v-sheet v-else-if="hasValue" key="filled" border rounded
             class="media-field-box media-field-filled" :style="boxStyle"
             @mouseenter="hover = true" @mouseleave="hover = false">
      <!-- Imagem: as ações cobrem a imagem no hover, que só precisa ser vista.
           Áudio: as ações ficam AO LADO do player — um overlay cobriria justamente
           o controle que o usuário tem de clicar para ouvir. -->
      <template v-if="type === 'image'">
        <v-img :src="value" :alt="label" :aria-label="label" height="100%"/>
        <v-overlay v-if="!readonly" contained :model-value="hover || !canHover" opacity="0.55" z-index="1"
                   class="align-center justify-center">
          <ontos-header-action icon="mdi-file-replace" :label="$tt('field.change')" always-label class="mr-2"
                               @click="pick"/>
          <ontos-header-action icon="mdi-delete" :label="$tt(kind.removeKey)" always-label @click="remove"/>
        </v-overlay>
      </template>

      <div v-else class="media-field-audio d-flex align-center px-3">
        <ontos-audio-player :src="value" :label="label"/>
        <span v-if="durationText" class="ml-3 text-caption text-grey">{{ durationText }}</span>
        <template v-if="!readonly">
          <v-btn size="small" variant="text" icon class="ml-auto" :title="$tt('field.change')" @click="pick">
            <v-icon size="small">mdi-file-replace</v-icon>
          </v-btn>
          <v-btn size="small" variant="text" icon :title="$tt(kind.removeKey)" @click="remove">
            <v-icon size="small">mdi-delete</v-icon>
          </v-btn>
        </template>
      </div>
    </v-sheet>

    <!-- vazio: a mesma caixa é o alvo do clique e do soltar. Em Vue 3 o listener posto
         num componente cai no elemento raiz dele, que é o que o .native fazia. -->
    <v-sheet v-else key="empty" border rounded :style="boxStyle"
             class="media-field-box media-field-drop d-flex flex-column align-center justify-center text-center pa-4"
             :class="{'media-field-over': over, 'media-field-disabled': readonly}"
             @click="pick" @dragover.prevent="onDragOver"
             @dragleave.prevent="over = false" @drop.prevent="onDrop">
      <v-icon size="40" color="grey">{{ kind.emptyIcon }}</v-icon>
      <div class="text-body-2 mt-2">{{ readonly ? $tt(kind.emptyKey) : $tt(kind.dropHintKey) }}</div>
    </v-sheet>

    <!-- opacity e não display:none: um input escondido com display:none não recebe
         focus(), e o CreateInstance foca o primeiro campo do formulário ao abrir -->
    <input ref="file" type="file" :accept="kind.accept" class="media-field-input" @change="onPick">

    <v-messages v-if="shownError" active :messages="shownError" color="error" class="mt-1" role="alert"/>
    <div v-else-if="!readonly" class="text-caption text-grey mt-1">{{ hint }}</div>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {MediaKind, mediaKindOf, prepareMedia} from '@/utils/mediaFile'
import {formatBytes} from '@/utils/imageFile'
import {formatDuration} from '@/utils/audioFile'
import {isPending, registerPending, revoke} from '@/services/mediaStorage'
import AudioPlayer from '@/components/shared/AudioPlayer.vue'

/**
 * Campo de mídia: escolhe, valida e pré-visualiza um arquivo que vai para o
 * Storage. Serve imagem e áudio.
 *
 * Um componente só, e não uma casca com slot mais dois wrappers: contando o
 * arquivo, exatamente UM elemento difere entre os dois tipos (o preview); todo o
 * resto difere por valor, e esses valores vêm do descritor em utils/mediaFile.
 * Wrappers teriam de redeclarar as props, reemitir os eventos e republicar o par
 * registerPending/replaceWith por slot com escopo — mais código e mais indireção.
 *
 * Não usa v-file-input de propósito. Aquele é um controle em forma de campo de
 * texto para LISTA de arquivos, e o validador do value dele só aceita File[] —
 * alimentá-lo com a URL era a origem do "Invalid prop" no console.
 *
 * O valor que entra e sai é sempre uma URL curta: https:// quando já salva, blob:
 * enquanto pendente. Quem sobe para o Storage é o EditInstanceService, no salvar.
 */
@Component({
  emits: ['input', 'change'],
  name: 'ontos-media-field',
  components: {'ontos-audio-player': AudioPlayer}
})
class MediaField extends Vue {
  @Prop({default: null}) readonly value!: string | null
  @Prop({type: String, default: ''}) readonly label!: string
  @Prop({type: Boolean, default: false}) readonly readonly!: boolean
  @Prop({type: String, default: ''}) readonly errorMessage!: string
  /** default 'image' para o call site antigo continuar valendo sem alteração */
  @Prop({type: String, default: 'image'}) readonly type!: string

  busy = false
  hover = false
  over = false
  localError = ''
  /** vem da sonda de validação; o áudio recém-escolhido não tem mais o que mostrar */
  duration = 0

  /** Sem hover (toque), o overlay tem de ficar sempre visível ou os botões somem */
  canHover = typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(hover: hover)').matches

  get kind(): MediaKind {
    return mediaKindOf(this.type)
  }

  get boxStyle(): object {
    return {height: this.kind.boxHeight}
  }

  get hasValue(): boolean {
    return !!this.value
  }

  get shownError(): string {
    return this.localError || this.errorMessage || ''
  }

  get durationText(): string {
    return this.duration ? formatDuration(this.duration) : ''
  }

  get hint(): string {
    return this.$tt(this.kind.hintKey, this.kind.hintValues()) as string
  }

  pick() {
    if (this.readonly || this.busy) return
    this.localError = ''
    const input = this.$refs.file as HTMLInputElement
    //sem isto, escolher o mesmo arquivo duas vezes seguidas não dispara change
    input.value = ''
    input.click()
  }

  onDragOver() {
    if (!this.readonly && !this.busy) this.over = true
  }

  onDrop(event: DragEvent) {
    this.over = false
    if (this.readonly || this.busy) return
    const files = event.dataTransfer && event.dataTransfer.files
    //arrastar e soltar ignora o accept por completo: quem barra é o prepare
    if (files && files.length) this.accepted(files[0])
  }

  onPick(event: Event) {
    const files = (event.target as HTMLInputElement).files
    if (files && files.length) this.accepted(files[0])
  }

  async accepted(file: File) {
    this.busy = true
    this.localError = ''
    try {
      const result = await prepareMedia(file, this.type)
      if (!result.ok) {
        this.localError = this.$tt(this.kind.errorKeys[result.code], {
          size: formatBytes(this.kind.maxBytes)
        }) as string
        return
      }
      this.duration = result.duration || 0
      this.replaceWith(registerPending(result.blob))
    } finally {
      this.busy = false
    }
  }

  remove() {
    if (this.readonly) return
    this.localError = ''
    this.duration = 0
    this.replaceWith(null)
  }

  /**
   * O Blob anterior só pode ser descartado aqui: neste ponto está provado que nada
   * mais aponta para ele. Fora deste caso quem revoga é o EditInstanceService,
   * depois de salvar — este componente é destruído e remontado pelo v-for do
   * formulário, e revogar ao destruir deixaria um blob: morto no modelo.
   *
   * Efeito colateral conhecido no áudio: ao salvar, o EditInstanceService troca o
   * blob: pela URL definitiva e revoga. Se o usuário estiver ouvindo o que acabou
   * de escolher, o som corta.
   */
  replaceWith(next: string | null) {
    const previous = this.value
    this.$emit('input', next)
    this.$emit('change', next)
    if (isPending(previous) && previous !== next) revoke(previous)
  }
}

export default toNative(MediaField)
</script>
<style scoped>
.media-field-box {
  position: relative;
  overflow: hidden;
}

.media-field-audio {
  height: 100%;
}

.media-field-drop {
  border-style: dashed !important;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.media-field-drop:hover,
.media-field-over {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(0, 0, 0, 0.03);
}

.media-field-disabled {
  cursor: default;
  opacity: 0.6;
}

.media-field-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
}
</style>
